<template>
    <v-card class="rounded-0" flat>
        <v-container class="pa-0">
            <v-row no-gutters>
                <!-- Index -->
                <v-col v-if="!isPhoneBool" cols="auto"
                    style="background-color: white; position: relative; width: 250px;">
                    <v-list class="py-0" style="position: fixed;  width: 250px;">
                        <h3 class="pa-4 pb-2">Sections</h3>
                        <v-list-item-group v-model="scrollItem">
                            <v-list-item v-for="section in sectionList" :key="'i' + section.key" :value="section.key"
                                dense>
                                <v-list-item-title>{{ section.title }}</v-list-item-title>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </v-col>

                <!-- Sections -->
                <v-col cols="0" style="border-left: 1px solid #CCCC;" class="pb-16">
                    <component :is="section.component || 'SettingsSection'" v-for="(section, index) in sectionList"
                        :key="section.key" :id="section.key" :section="section" :index="index" :level="0"
                        :isLoading="isLoading" :initiated="initiated" class="mx-4 py-4"
                        @debounceSave="debouncedAutoSave()" @instantSave="saveSettings">
                    </component>
                </v-col>
            </v-row>
        </v-container>
    </v-card>
</template>


<script>
import Vue from 'vue'
import { debounce } from "debounce";
import clientAPI from "@/api/ClientAPIMixin";
import SettingsConstants from "@/helpers/Constants/SettingsConstants";
import Section from "./Section.js"
import SettingsMixin from './SettingsMixin';
import SettingsSection from './SettingsSection.vue';
import CompanySettingsSection from './CompanySettingsSection.vue'
import ServiceArticleSection from './ServiceArticleSection.vue';
import EmailSection from './EmailSection.vue';
import ErpExportSection from './ErpExportSection.vue';

const sections = [
    new Section({
        key: 'ServiceProvider',
        title: 'Company details',
        description: "These details define your company to the system. \n"
            + "Contact information is displayed to end customers in contact sections and reports. \n"
            + "Address is also used to center the map in the planning module. ",
        settingList: [
            SettingsConstants.ServiceProviderCompanyName,
            SettingsConstants.ServiceProviderTelephone,
            SettingsConstants.ServiceProviderEmail,
            SettingsConstants.ServiceProviderAddressStreet,
            SettingsConstants.ServiceProviderAddressZipAndCity,
            SettingsConstants.ServiceProviderMainLocationCoordinates,
            SettingsConstants.ServiceProviderMainStoreId,
        ],
        component: 'CompanySettingsSection'
    }),
    new Section({
        key: 'JobDefaultValues',
        title: 'Job default values',
        description: 'Default field values when creating new jobs.',
        settingList: [
            SettingsConstants.DefaultEstimatedTime,
            SettingsConstants.DefaultJobTypeId,
            SettingsConstants.DefaultPriorityId,
            SettingsConstants.DefaultStatusId,
            SettingsConstants.DefaultJobCategoryId,
        ],
    }),
    new Section({
        key: 'TimeReportArticles',
        title: 'Service articles',
        description: "Articles used for time tracking and other service charges.",
        settingList: [
            SettingsConstants.InternalArticles,
            SettingsConstants.TimeReportDefaultWorkArticle,
            SettingsConstants.TimeReportValidWorkArticles,
            SettingsConstants.TimeReportDefaultDrivingArticle,
            SettingsConstants.TimeReportValidDrivingArticles,
            SettingsConstants.TimeReportDefaultDrivingDictanceArticle,
            SettingsConstants.TimeReportDefaultDrivingCostParkingArticle,
            SettingsConstants.TimeReportDefaultDrivingCostRoadTaxArticle,
            SettingsConstants.TimeReportDefaultDrivingCostZoneArticle,
            SettingsConstants.TimeReportDefaultGenericArticle,
            SettingsConstants.TimeReportDefaultMiscPartCostArticle,
        ],
        component: ServiceArticleSection,
    }),
    new Section({
        key: 'TimeReport',
        title: 'Time reporting',
        description: "Settings for handling time articles in the system.",
        subSections: [
            new Section({
                key: 'TimeReportWorkRounding',
                title: 'Work time rounding',
                description: "Settings used for rounding start/stop work timers in the technician app.",
                settingList: [
                    SettingsConstants.TimeReportWorkTimeRounding,
                    SettingsConstants.TimeReportWorkMinValueTimeRounding,
                    SettingsConstants.TimeReportWorkTimeMinutesIncurred,
                ],
                component: 'TimeRoundingSection'
            }),
            new Section({
                key: 'TimeReportDriveRounding',
                title: 'Drive time rounding',
                description: "Settings used for rounding start/stop drive timers in the technician app.",
                settingList: [
                    SettingsConstants.TimeReportDriveTimeRounding,
                    SettingsConstants.TimeReportDriveMinValueTimeRounding,
                    SettingsConstants.TimeReportDriveTimeMinutesIncurred,
                ],
                component: 'TimeRoundingSection'
            }),
        ],
        settingList: [
            SettingsConstants.TimeReportInternalCustomer,
            SettingsConstants.TimeReportIncludeTimeDescriptionWorkDrive
        ],
    }),

    new Section({
        key: 'Permissions',
        title: 'Permissions',
        description: 'Control user permissions and access.',
        settingList: [
            SettingsConstants.TechnicianCanApproveJob,
            SettingsConstants.TechnicianLimitSelectToOwnStore,
            SettingsConstants.IsSerialNumberEditingRestricted,
        ],
    }),

    new Section({
        key: 'PreventiveMaintenance',
        title: 'Preventive maintenance',
        description: 'Settings for preventive maintenance and service agreements.\n'
            + 'These defaults apply when creating new service agreements and PM jobs.',
        settingList: [
            SettingsConstants.PreventiveMaintenanceEnabled,
            SettingsConstants.DefaultPreventiveMaintenancePriorityId,
            SettingsConstants.PreventiveMaintenanceMachineAffinityDays,
            SettingsConstants.DefaultServiceAgreementNumberOfTechnicianRequired,
            SettingsConstants.DefaultServiceAgreementHoursOfWorkEachTechnician,
            SettingsConstants.DefaultServiceAgreementReoccuranceHowOften,
            SettingsConstants.GroupPMJobs,
        ],
    }),

    new Section({
        key: 'Email',
        title: 'Email',
        description: '',
        settingList: [],
        component: 'EmailSection'
    }),

    new Section({
        key: 'ErpExport',
        title: 'ERP Export',
        description: 'Configure the ERP export plugin used for exporting data.',
        settingList: [
            SettingsConstants.ErpFileExportEnabled,
            SettingsConstants.ErpExportAssembly,
        ],
        component: 'ErpExportSection'
    }),
];

/*
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
*/

export default {
    name: "DefaultSettings",
    components: {
        SettingsSection,
        CompanySettingsSection,
        ServiceArticleSection,
        EmailSection,
        ErpExportSection,
    },
    mixins: [clientAPI, SettingsMixin],

    data() {
        return {
            scrollItem: null,
            initiated: false,
            isLoading: false,
            sectionList: sections,
            settingList: [],
        };
    },

    created() {
        // debounce to 1 second
        this.debouncedAutoSave = debounce(() => {
            const dirty = this.getDirtySettings();
            this.saveSettings(dirty);
        }, 1000);
    },

    mounted() {
        sections.forEach(s => {
            this.settingList.push(...s.getFlatSettingList());
        });
        //console.log('settingList', this.settingList)
        this.fetchSettings();
    },

    methods: {
        getDirtySettings() {
            return this.settingList.filter(s => s._dirty);
        },

        fetchSettings() {
            var groups = new Set(this.settingList.map(obj => obj.group));
            this.isLoading = true;

            clientAPI.methods.getSettingsListByGroup({ groups: [...groups] }).then(response => {
                //map the setting values from the reponse onto the setting list items
                this.settingList.forEach(setting => {
                    var newVal = response[setting.group.toLowerCase()]?.[setting.settingKey];
                    this.settingValueFromString(setting, newVal)
                });
                //also re-fetch globalsettings in the background if necessary
                if (this.settingList.some(x => x.globalSettingsKey)) {
                    this.$store.dispatch("getUserInfo", true);
                }
                this.isLoading = false;
                this.initiated = true;
            });
        },

        async saveSettings(settingsToSave) {
            // If called manually with no args, still only save valid ones
            const baseList = settingsToSave && settingsToSave.length ? settingsToSave : this.settingList;

            const list = baseList.filter(s => this.isSettingValid(s));
            if (!list.length) {
                // nothing valid to save
                return;
            }

            const payload = {};
            list.forEach(setting => {
                Vue.set(setting, 'loading', true)
                this.settingValueToString(setting, payload);
                //console.log(setting, setting.value, value);
            });
            //await delay(3000);
            console.log('sending payload', payload)
            await clientAPI.methods.setSettingsList(payload)
                .then(() => {
                    // clear dirty flags only for what we just saved
                    list.forEach(s => {
                        s._dirty = false;
                        s.loading = false;
                        s.fetchValue = s.value;
                    });
                    //also re-fetch globalsettings in the background if necessary
                    if (list.some(x => x.globalSettingsKey)) {
                        this.$store.dispatch("getUserInfo", true);
                    }
                });
        },

        scrollToItem(name) {
            var element = document.getElementById(name);
            if (element) {
                element.scrollIntoView();
            }
        },
    },

    computed: {},

    watch: {
        scrollItem: function (val) {
            this.scrollToItem(val)
        }
    },
};
</script>


<style lang="scss" scoped></style>